// ============================================================================
// warlordRender.js — the named officers (warlords, generals, the Dynast, the
// Warmaster) are FEW, so unlike the grunts they are NOT instanced: each is a
// hand-dressed CHARACTER — a per-name config (helm / horns / hood / cowl /
// plume / crown, bulk, cape, robe, faction palette) layered over a shared
// articulated rig (hip→knee, shoulder→elbow joint groups, pivot at the top of
// each limb) with a unique weapon and a weapon-specific attack animation
// (chop / heavy smash / thrust / sweep / twin-alternating). crowd.js skips
// UNIT_WARLORD so they aren't drawn twice. All animation is CPU (they're ~10
// meshes on field at most).
// ============================================================================

import * as THREE from 'three';
import { lerp, clamp01, angleDamp } from '../core/math.js';
import {
  ST_ATTACK, ST_DEAD, ST_STUN, ST_LAUNCH, ST_KNOCK, ST_BLOCK, TICK_DT, TEAM_PLAYER,
} from '../core/constants.js';

const _texLoader = new THREE.TextureLoader();
const _tex = {};
function loadTex(url) {
  if (_tex[url]) return _tex[url];
  const t = _texLoader.load(url);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return (_tex[url] = t);
}
// materials are cached + shared across all officers (cheap state changes)
const _mats = new Map();
function lam(color, o = {}) {
  const k = color + '|' + (o.map || '') + '|' + (o.emissive || 0) + '|' + (o.ei || 0);
  let m = _mats.get(k);
  if (m) return m;
  m = new THREE.MeshLambertMaterial({
    color, map: o.map ? loadTex(o.map) : null,
    emissive: o.emissive || 0x000000, emissiveIntensity: o.ei || 0,
  });
  _mats.set(k, m);
  return m;
}

// ---- faction palettes (frost/carrion commanders read as their own host) ----
const PALETTES = {
  host:    { armor: 0xc0bac6, map: 'art/armor_host.png',   ember: 0x2a3c6a, cloth: 0x14171d, skin: 0xb99a80 },
  magmar:  { armor: 0xc0bac6, map: 'art/armor_magmar.png', ember: 0xff5210, cloth: 0x2a1216, skin: 0xa8836a },
  frost:   { armor: 0xe2ecf5, map: 'art/armor_host.png',   ember: 0x6fb0e6, cloth: 0x1a2430, skin: 0xc4b2a2 },
  carrion: { armor: 0xd8dcc0, map: 'art/armor_magmar.png', ember: 0x8fbf40, cloth: 0x181f10, skin: 0x9a8f78 },
};

// ---- per-character identity: silhouette + dressing + accent ---------------
// helm: greathelm | horned | crowned | crown | hood | cowl | plume | bare
const CHARACTERS = {
  // Ashen Host generals
  'Haldric Vane':    { helm: 'crowned',   trim: 0xd7dbe0, cape: 0x1a2233, capeLen: 1.5,  bulk: 1.06 },
  'Sera Volk':       { helm: 'hood',      trim: 0x9aa4b8, cape: 0x101318, capeLen: 1.25, bulk: 0.86, slim: true },
  'Old Garrick':     { helm: 'bare',      trim: 0x8a8478, cape: 0x232019, capeLen: 1.1,  bulk: 1.2,  hair: 0x9a938a, beard: true },
  'Lysa Fenn':       { helm: 'plume',     trim: 0xc8ccd4, cape: 0x1c2426, capeLen: 0.9,  bulk: 0.84, plume: 0xd8dfe8, quiver: true },
  'Bram Dagor':      { helm: 'greathelm', trim: 0xb8bfcc, cape: 0x1d1f28, capeLen: 1.2,  bulk: 1.14, towerPauls: true },
  'Coran Vael':      { helm: 'plume',     trim: 0xe2e6ec, cape: 0x2a3248, capeLen: 1.35, bulk: 1.0,  plume: 0xbfc9dc },
  'Mother Isolde':   { helm: 'cowl',      trim: 0x9a9284, cape: 0x17141a, capeLen: 1.3,  bulk: 0.94, robe: true },
  // Magmar warlords
  'Ashlord Kaevun':  { helm: 'horned',    trim: 0xffb257, cape: 0x2a1114, capeLen: 1.4,  bulk: 1.1 },
  'Vurmog':          { helm: 'bare',      trim: 0xff8a3a, cape: 0x241012, capeLen: 0.9,  bulk: 1.24, hair: 0x3a2a22, beard: true },
  'Draxa Embermaw':  { helm: 'cowl',      trim: 0xff7a2a, cape: 0x1c0f14, capeLen: 1.35, bulk: 0.96, robe: true },
  'Tholvar':         { helm: 'greathelm', trim: 0xffa04a, cape: 0x201014, capeLen: 1.1,  bulk: 0.88, slim: true },
  'Skarn Duraghast': { helm: 'horned',    trim: 0xff6a1a, cape: 0x26120e, capeLen: 1.2,  bulk: 1.3,  towerPauls: true },
  'Ysra Coalheart':  { helm: 'plume',     trim: 0xff9a4a, cape: 0x28100f, capeLen: 1.15, bulk: 0.88, plume: 0xff5a12 },
  // Frost / Carrion invasion commanders (Conquest)
  'Jarl Hoarfrost':  { helm: 'horned',    trim: 0xcfe6f6, cape: 0x18222e, capeLen: 1.4,  bulk: 1.22, pal: 'frost', towerPauls: true },
  'Yrsa Palewind':   { helm: 'plume',     trim: 0xd8ecf8, cape: 0x1a2430, capeLen: 1.1,  bulk: 0.86, pal: 'frost', plume: 0xcfe6f4, slim: true },
  'Gravelord Murk':  { helm: 'cowl',      trim: 0xb9cc7a, cape: 0x161c10, capeLen: 1.35, bulk: 1.12, pal: 'carrion', robe: true },
  'Sythe the Hollow': { helm: 'hood',     trim: 0xa9c06a, cape: 0x131a0e, capeLen: 1.2,  bulk: 0.84, pal: 'carrion', slim: true },
  // the Dynast
  'Ourang-Vael':     { helm: 'crown',     trim: 0xffc06a, cape: 0x2e0f0a, capeLen: 1.6,  bulk: 1.2 },
};
const DEFAULT_CHAR = { helm: 'greathelm', trim: 0xffb257, cape: 0x241216, capeLen: 1.2, bulk: 1.0 };

const box = (w, h, d, mat) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);

// ---- unique weapons (built into a hand group, extending up-forward) --------
function buildWeapon(hand, type, wmat, trim, ember) {
  const add = (w, h, d, mat, x, y, z, rx) => { const m = box(w, h, d, mat); m.position.set(x, y, z); if (rx) m.rotation.x = rx; hand.add(m); return m; };
  switch (type) {
    case 'halberd':
      add(0.06, 2.3, 0.06, wmat, 0, 0.6, 0);        // shaft
      add(0.52, 0.48, 0.07, wmat, 0.24, 1.55, 0);   // axe head
      add(0.06, 0.46, 0.06, trim, 0, 1.98, 0);      // top spike
      add(0.3, 0.07, 0.07, trim, -0.16, 1.35, 0);   // back beak
      break;
    case 'cleaver':
      add(0.1, 0.8, 0.1, wmat, 0, 0.35, 0);         // haft
      add(0.56, 0.82, 0.08, wmat, 0.08, 1.06, 0);   // huge broad blade
      add(0.62, 0.09, 0.1, trim, 0.08, 0.6, 0);     // spine
      add(0.2, 0.2, 0.09, trim, 0.3, 1.5, 0);       // notch tooth
      break;
    case 'scythe':
      add(0.06, 2.25, 0.06, wmat, 0, 0.55, 0);      // shaft
      add(0.85, 0.11, 0.07, wmat, 0.38, 1.62, 0);   // blade
      add(0.34, 0.11, 0.07, wmat, 0.72, 1.44, 0, 0.7); // curved tip
      add(0.09, 0.2, 0.08, trim, 0.06, 1.5, 0);     // binding
      break;
    case 'maul':
      add(0.1, 1.75, 0.1, wmat, 0, 0.4, 0);         // haft
      add(0.46, 0.48, 0.46, wmat, 0, 1.38, 0);      // head block
      add(0.52, 0.1, 0.52, trim, 0, 1.16, 0);       // collar
      add(0.06, 0.36, 0.06, trim, 0, 1.78, 0);      // top spike
      break;
    case 'spear':
      add(0.055, 2.55, 0.055, wmat, 0, 0.72, 0);    // long shaft
      add(0.12, 0.58, 0.06, trim, 0, 2.05, 0);      // leaf blade
      add(0.16, 0.07, 0.07, trim, 0, 1.72, 0);      // lugs
      break;
    case 'twin': case 'sword':
      add(0.12, 1.28, 0.05, wmat, 0, 0.56, 0);
      add(0.28, 0.09, 0.15, trim, 0, -0.02, 0);
      add(0.08, 0.14, 0.09, trim, 0, -0.16, 0);     // pommel
      break;
    case 'dynast':
      add(0.09, 2.7, 0.09, wmat, 0, 0.72, 0);        // great shaft
      add(0.38, 1.15, 0.08, trim, 0, 1.55, 0);       // massive molten blade
      add(0.1, 1.15, 0.1, new THREE.MeshBasicMaterial({ color: ember }), 0.14, 1.55, 0); // molten edge
      add(0.56, 0.13, 0.12, wmat, 0, 0.92, 0);       // crossguard
      break;
    case 'greatsword':
    default:
      add(0.2, 2.05, 0.06, wmat, 0, 0.98, 0);        // long broad blade
      add(0.46, 0.12, 0.16, trim, 0, -0.02, 0);      // crossguard
      add(0.11, 0.22, 0.13, trim, 0, -0.22, 0);      // pommel
      break;
  }
}

// ---- the shared articulated rig, dressed per character ---------------------
export function buildWarlordMesh(u) {
  const name = (u.warlord && u.warlord.name) || '';
  const weaponType = (u.warlord && u.warlord.weapon) || 'greatsword';
  const C = CHARACTERS[name] || DEFAULT_CHAR;
  const P = PALETTES[C.pal || (u.team === TEAM_PLAYER ? 'host' : 'magmar')];

  const armor = lam(P.armor, { map: P.map, emissive: P.ember, ei: 0.2 });
  const trim = lam(C.trim, { emissive: C.trim, ei: 0.22 });
  const cloth = lam(P.cloth);
  const capeM = lam(C.cape);
  const skin = lam(P.skin);
  const wmat = lam(0x55555f, { map: 'art/weapon.png', emissive: 0x160b06, ei: 0.45 });

  const bulk = C.bulk || 1, slim = C.slim ? 0.9 : 1;
  const tw = 0.74 * bulk;                       // torso width
  const group = new THREE.Group();
  group.rotation.order = 'YXZ';

  // ---- legs: hip group → thigh → knee group → shin + foot ----
  const mkLeg = (side) => {
    const hip = new THREE.Group(); hip.position.set(side * 0.18 * bulk, 0.98, 0);
    const thigh = box(0.26 * bulk * slim, 0.52, 0.28, armor); thigh.position.set(0, -0.26, 0); hip.add(thigh);
    const knee = new THREE.Group(); knee.position.set(0, -0.5, 0); hip.add(knee);
    const shin = box(0.22 * bulk * slim, 0.5, 0.24, armor); shin.position.set(0, -0.24, 0); knee.add(shin);
    const foot = box(0.24 * bulk, 0.12, 0.36, cloth); foot.position.set(0, -0.46, 0.05); knee.add(foot);
    return { hip, knee };
  };
  const L = mkLeg(1), R = mkLeg(-1);

  // ---- torso / dressing ----
  const torso = box(tw, 0.78, 0.42, armor); torso.position.set(0, 1.34, 0);
  const chest = box(tw * 0.78, 0.36, 0.46, trim); chest.position.set(0, 1.46, 0.02);
  const belt = box(tw * 1.06, 0.13, 0.46, cloth); belt.position.set(0, 1.0, 0);
  const buckle = box(0.14, 0.14, 0.05, trim); buckle.position.set(0, 1.0, 0.24);
  group.add(torso, chest, belt, buckle);
  if (C.robe) {                                   // long skirt over the legs (Isolde, Draxa, Murk)
    const skirtF = box(tw * 1.02, 0.85, 0.1, capeM); skirtF.position.set(0, 0.55, 0.2); group.add(skirtF);
    const skirtB = box(tw * 1.02, 0.85, 0.1, capeM); skirtB.position.set(0, 0.55, -0.2); group.add(skirtB);
    const skirtL = box(0.1, 0.85, 0.34, capeM); skirtL.position.set(tw * 0.5, 0.55, 0); group.add(skirtL);
    const skirtR = box(0.1, 0.85, 0.34, capeM); skirtR.position.set(-tw * 0.5, 0.55, 0); group.add(skirtR);
  }
  if (C.quiver) {                                 // Lysa's quiver across the back
    const q = box(0.16, 0.62, 0.16, cloth); q.position.set(-0.2, 1.5, -0.3); q.rotation.z = 0.5; group.add(q);
    const fl = box(0.2, 0.1, 0.2, trim); fl.position.set(-0.38, 1.74, -0.3); fl.rotation.z = 0.5; group.add(fl);
  }

  // ---- pauldrons ----
  const paulW = C.towerPauls ? 0.42 : 0.34, paulH = C.towerPauls ? 0.4 : 0.28;
  const paulL = box(paulW, paulH, 0.42, trim); paulL.position.set(0.5 * bulk, 1.68, 0);
  const paulR = box(paulW, paulH, 0.42, trim); paulR.position.set(-0.5 * bulk, 1.68, 0);
  group.add(paulL, paulR);
  if (C.towerPauls) {                             // second layered plate
    const p2L = box(0.34, 0.2, 0.36, armor); p2L.position.set(0.52 * bulk, 1.48, 0); group.add(p2L);
    const p2R = box(0.34, 0.2, 0.36, armor); p2R.position.set(-0.52 * bulk, 1.48, 0); group.add(p2R);
  }

  // ---- head + helm identity ----
  const headG = new THREE.Group(); headG.position.set(0, 1.94, 0); group.add(headG);
  const head = box(0.34, 0.36, 0.34, C.helm === 'bare' ? skin : armor); headG.add(head);
  switch (C.helm) {
    case 'greathelm': {
      const visor = box(0.36, 0.09, 0.06, cloth); visor.position.set(0, 0.03, 0.16); headG.add(visor);
      const ridge = box(0.06, 0.12, 0.38, trim); ridge.position.set(0, 0.22, 0); headG.add(ridge);
      break;
    }
    case 'horned': {
      const visor = box(0.36, 0.09, 0.06, cloth); visor.position.set(0, 0.03, 0.16); headG.add(visor);
      const hornL = box(0.09, 0.4, 0.09, trim); hornL.position.set(0.24, 0.3, 0); hornL.rotation.z = -0.45; headG.add(hornL);
      const hornR = box(0.09, 0.4, 0.09, trim); hornR.position.set(-0.24, 0.3, 0); hornR.rotation.z = 0.45; headG.add(hornR);
      break;
    }
    case 'crowned': {                              // the Warmaster's circlet
      const band = box(0.4, 0.08, 0.4, trim); band.position.set(0, 0.16, 0); headG.add(band);
      for (const sx of [-0.14, 0, 0.14]) { const spike = box(0.05, 0.14, 0.05, trim); spike.position.set(sx, 0.27, 0.14); headG.add(spike); }
      break;
    }
    case 'crown': {                                // the Dynast's jagged molten crown
      const band = box(0.44, 0.1, 0.44, trim); band.position.set(0, 0.2, 0); headG.add(band);
      for (const [sx, sz] of [[0.17, 0.17], [-0.17, 0.17], [0.17, -0.17], [-0.17, -0.17], [0, 0.2]]) {
        const spike = box(0.06, 0.24, 0.06, trim); spike.position.set(sx, 0.36, sz); headG.add(spike);
      }
      const eyes = box(0.3, 0.05, 0.03, new THREE.MeshBasicMaterial({ color: P.ember })); eyes.position.set(0, 0.02, 0.18); headG.add(eyes);
      break;
    }
    case 'hood': {
      const hood = box(0.42, 0.42, 0.42, capeM); hood.position.set(0, 0.06, -0.04); headG.add(hood);
      const face = box(0.26, 0.22, 0.05, cloth); face.position.set(0, -0.02, 0.18); headG.add(face);
      break;
    }
    case 'cowl': {
      const cowl = box(0.46, 0.5, 0.46, capeM); cowl.position.set(0, 0.1, -0.05); headG.add(cowl);
      const dark = box(0.3, 0.26, 0.05, cloth); dark.position.set(0, -0.02, 0.19); headG.add(dark);
      const glow = box(0.2, 0.04, 0.03, new THREE.MeshBasicMaterial({ color: P.ember })); glow.position.set(0, 0.0, 0.22); headG.add(glow);
      break;
    }
    case 'plume': {
      const ridge = box(0.07, 0.1, 0.4, trim); ridge.position.set(0, 0.22, 0); headG.add(ridge);
      const plume = box(0.07, 0.34, 0.5, lam(C.plume || 0xd8dfe8, { emissive: C.plume || 0xd8dfe8, ei: 0.15 }));
      plume.position.set(0, 0.36, -0.16); plume.rotation.x = 0.35; headG.add(plume);
      break;
    }
    case 'bare': {
      const hairM = lam(C.hair || 0x4a3a2c);
      const hair = box(0.36, 0.12, 0.36, hairM); hair.position.set(0, 0.22, -0.02); headG.add(hair);
      if (C.beard) { const beard = box(0.28, 0.22, 0.08, hairM); beard.position.set(0, -0.16, 0.16); headG.add(beard); }
      break;
    }
  }

  // ---- cape (pivot at the shoulders so it can sway) ----
  const capeLen = C.capeLen || 1.2;
  const cape = new THREE.Group(); cape.position.set(0, 1.78, -0.26); group.add(cape);
  const capeMain = box(tw * 1.0, capeLen, 0.05, capeM); capeMain.position.set(0, -capeLen / 2, 0); cape.add(capeMain);
  const capeTip = box(tw * 0.8, 0.28, 0.05, capeM); capeTip.position.set(0, -capeLen - 0.12, 0.015); cape.add(capeTip); // tattered taper

  // ---- arms: shoulder group → upper → elbow group → forearm + hand ----
  const mkArm = (side) => {
    const sh = new THREE.Group(); sh.position.set(side * (0.5 * bulk), 1.62, 0);
    sh.rotation.order = 'YXZ';   // lift (x) THEN yaw (y) — horizontal sweeps fan properly
    const upper = box(0.2 * slim, 0.42, 0.22, armor); upper.position.set(0, -0.2, 0); sh.add(upper);
    const elb = new THREE.Group(); elb.position.set(0, -0.42, 0); sh.add(elb);
    const fore = box(0.18 * slim, 0.4, 0.2, armor); fore.position.set(0, -0.19, 0); elb.add(fore);
    const fist = box(0.15, 0.15, 0.15, skin); fist.position.set(0, -0.42, 0.02); elb.add(fist);
    return { sh, elb };
  };
  const AL = mkArm(1), AR = mkArm(-1);
  const hand = new THREE.Group(); hand.position.set(0, -0.44, 0.12); AR.elb.add(hand);
  buildWeapon(hand, weaponType, wmat, trim, P.ember);
  if (weaponType === 'twin') {
    const handL = new THREE.Group(); handL.position.set(0, -0.44, 0.12); AL.elb.add(handL);
    buildWeapon(handL, 'sword', wmat, trim, P.ember);
  }

  group.add(L.hip, R.hip, AL.sh, AR.sh, cape);
  return {
    group,
    parts: {
      legL: L.hip, legR: R.hip, kneeL: L.knee, kneeR: R.knee,
      shL: AL.sh, shR: AR.sh, elbL: AL.elb, elbR: AR.elb,
      torso, head: headG, cape,
    },
    style: ATTACK_STYLE[weaponType] || 'chop',
    torsoY: 1.34,
  };
}

// which attack animation each weapon uses
const ATTACK_STYLE = {
  spear: 'thrust', halberd: 'chop', dynast: 'heavy', maul: 'heavy',
  greatsword: 'chop', sword: 'chop', cleaver: 'sweep', scythe: 'sweep', twin: 'alt',
};

export class WarlordRenderer {
  constructor(scene) {
    this.scene = scene;
    this.meshes = new Map(); // unit.id -> { group, parts, style, phase, renderHeading, deadT, breath }
  }

  update(world, alpha, dt) {
    const seen = new Set();
    const list = world.warlords.slice();
    for (let i = 0; i < world.generals.length; i++) list.push(world.generals[i]);
    if (world.dynast) list.push(world.dynast);
    if (world.warmaster) list.push(world.warmaster);

    for (let i = 0; i < list.length; i++) {
      const u = list[i];
      if (!u._active || seen.has(u.id)) continue;
      seen.add(u.id);
      let m = this.meshes.get(u.id);
      if (!m) {
        m = buildWarlordMesh(u);
        m.phase = u.animPhase * 6.28; m.renderHeading = u.heading; m.deadT = 0; m.breath = u.animPhase * 6;
        this.scene.add(m.group);
        this.meshes.set(u.id, m);
      }
      const x = lerp(u.px, u.x, alpha), y = lerp(u.py, u.y, alpha), z = lerp(u.pz, u.z, alpha);
      m.renderHeading = angleDamp(m.renderHeading, u.heading, 12, dt);
      m.group.position.set(x, y, z);
      m.group.rotation.set(0, m.renderHeading, 0);
      m.group.scale.setScalar(u.scale);
      this._animate(m, u, dt);
      m.group.visible = true;
    }

    // drop meshes whose officer has despawned
    for (const [id, m] of this.meshes) {
      if (!seen.has(id)) { this.scene.remove(m.group); this.meshes.delete(id); }
    }
  }

  _animate(m, u, dt) {
    const P = m.parts;
    m.breath += dt;
    // ---- reset pose (guard stance: weapon arm raised, elbow bent) ----
    P.legL.rotation.x = 0; P.legR.rotation.x = 0;
    P.kneeL.rotation.x = 0; P.kneeR.rotation.x = 0;
    P.shR.rotation.set(-0.5, 0, 0); P.shL.rotation.set(-0.2, 0, 0.1);
    P.elbR.rotation.set(-0.55, 0, 0); P.elbL.rotation.set(-0.25, 0, 0);
    P.torso.rotation.set(0, 0, 0);
    P.torso.position.y = m.torsoY;
    P.head.rotation.set(0, 0, 0);
    P.cape.rotation.x = 0.05;
    m.group.rotation.x = 0;

    if (u.state === ST_DEAD) {
      m.deadT += dt;
      m.group.rotation.x = Math.min(m.deadT * 2.2, 1.5);
      P.shR.rotation.x = -0.2; P.shL.rotation.x = -0.3;
      P.elbR.rotation.x = 0; P.elbL.rotation.x = 0;
      P.cape.rotation.x = -0.4;
      return;
    }
    // launched / knocked ragdolls tumble like the grunts do
    if (u.state === ST_LAUNCH || u.state === ST_KNOCK) {
      m.group.rotation.x = u.tumble;
      P.shL.rotation.x = P.shR.rotation.x = -1.2;
      P.legL.rotation.x = 0.5; P.legR.rotation.x = -0.4;
      return;
    }
    if (u.state === ST_STUN) {
      P.torso.rotation.x = 0.32;
      P.head.rotation.x = 0.3;
      P.shR.rotation.x = 0.1; P.shL.rotation.x = 0.1;
      P.elbR.rotation.x = -0.1;
      return;
    }
    if (u.state === ST_BLOCK) {
      P.shR.rotation.set(-1.1, 0, -0.2);
      P.elbR.rotation.x = -1.25;
      P.shL.rotation.set(-0.75, 0, 0.35);
      P.elbL.rotation.x = -0.9;
      P.legL.rotation.x = 0.22; P.legR.rotation.x = -0.22;
      P.kneeL.rotation.x = 0.25; P.kneeR.rotation.x = 0.25;
      return;
    }
    if (u.state === ST_ATTACK) {
      this._attack(m, u);
      return;
    }

    // ---- walk / idle ----
    const spd = Math.hypot(u.x - u.px, u.z - u.pz) / TICK_DT;
    m.phase += (0.5 + spd) * dt * 2.2;
    const amt = Math.min(1, spd / 4);
    const sw = Math.sin(m.phase) * amt;
    P.legL.rotation.x = sw * 0.55; P.legR.rotation.x = -sw * 0.55;
    P.kneeL.rotation.x = Math.max(0, -sw) * 0.9;      // knee bends on the recovering leg
    P.kneeR.rotation.x = Math.max(0, sw) * 0.9;
    P.shL.rotation.x = -0.2 - sw * 0.3;
    P.shR.rotation.x = -0.5 + sw * 0.15;
    P.cape.rotation.x = 0.05 + amt * 0.4 + Math.sin(m.phase * 0.5) * 0.03;   // cape trails with speed
    m.group.position.y += Math.abs(Math.sin(m.phase)) * 0.045 * amt * u.scale;
    // idle breathing
    P.torso.position.y = m.torsoY + Math.sin(m.breath * 1.7) * 0.01;
  }

  // weapon-specific strike animation, phase-chained by the AI (u.atkPhase)
  _attack(m, u) {
    const P = m.parts;
    const t = u.stateT;
    switch (m.style) {
      case 'heavy': {                              // maul / dynast blade — huge overhead smash
        const a = clamp01(t * 2.0), s = Math.sin(a * Math.PI);
        P.shR.rotation.x = -2.4 + s * 3.6;
        P.elbR.rotation.x = -0.6 + s * 0.8;
        P.shL.rotation.x = -0.8 + s * 0.5;
        m.group.rotation.x = s * 0.2;
        P.kneeL.rotation.x = P.kneeR.rotation.x = s * 0.35;   // sink into the blow
        P.cape.rotation.x = 0.1 + s * 0.5;
        break;
      }
      case 'thrust': {                             // spear — lunge and extend
        const a = clamp01(t * 2.6), s = Math.sin(a * Math.PI);
        P.shR.rotation.x = -1.25 - s * 0.35;
        P.elbR.rotation.x = -1.2 + s * 1.2;        // arm extends through the thrust
        P.torso.rotation.y = -0.35 + s * 0.55;     // hip/shoulder drive
        P.shL.rotation.x = -0.5 - s * 0.3;
        P.legL.rotation.x = 0.35; P.legR.rotation.x = -0.3;
        m.group.rotation.x = s * 0.1;
        break;
      }
      case 'sweep': {                              // scythe / cleaver — wide horizontal reap
        const a = clamp01(t * 2.3), s = Math.sin(a * Math.PI);
        P.shR.rotation.x = -1.25;
        P.shR.rotation.y = 1.0 - s * 2.1;          // arm fans across, left → right
        P.elbR.rotation.x = -0.35;
        P.torso.rotation.y = -0.4 + s * 0.8;       // torso counter-twist
        P.shL.rotation.x = -0.6;
        P.legL.rotation.x = 0.25; P.legR.rotation.x = -0.25;
        P.cape.rotation.x = 0.1 + s * 0.35;
        break;
      }
      case 'alt': {                                // twin blades — alternating slashes
        const a = clamp01(t * 2.6), s = Math.sin(a * Math.PI);
        const rightSwing = (u.atkPhase % 2) === 0;
        const swingArm = rightSwing ? P.shR : P.shL, swingElb = rightSwing ? P.elbR : P.elbL;
        const offArm = rightSwing ? P.shL : P.shR, offElb = rightSwing ? P.elbL : P.elbR;
        swingArm.rotation.x = -1.9 + s * 3.0;
        swingArm.rotation.z = (rightSwing ? -1 : 1) * s * 0.5;
        swingElb.rotation.x = -0.7 + s * 0.9;
        offArm.rotation.x = -0.9 - s * 0.3;        // off-hand winds up the next cut
        offElb.rotation.x = -0.9;
        P.torso.rotation.y = (rightSwing ? 1 : -1) * (0.25 - s * 0.5);
        P.legL.rotation.x = 0.25; P.legR.rotation.x = -0.25;
        break;
      }
      default: {                                   // chop — greatsword / halberd / sword
        const a = clamp01(t * 2.4), s = Math.sin(a * Math.PI);
        P.shR.rotation.x = -1.9 + s * 3.0;
        P.elbR.rotation.x = -0.85 + s * 1.1;       // elbow whips through the cut
        P.shL.rotation.x = -0.35 + s * 0.3;
        P.legL.rotation.x = 0.25; P.legR.rotation.x = -0.25;
        m.group.rotation.x = s * 0.14;
        P.cape.rotation.x = 0.1 + s * 0.3;
        break;
      }
    }
  }
}
